import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  XMarkIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  TruckIcon,
  HomeIcon,
  UserGroupIcon,
  StarIcon,
  IdentificationIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Booking } from '../../types';
import { DateDisplay } from '../../components/Forms';
import BoardingQrCode from '../../components/Boarding/BoardingQrCode';

interface BookingDetailsProps {
  bookingId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (booking: Booking) => void;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({ 
  bookingId, 
  isOpen, 
  onClose, 
  onEdit 
}) => {
  const [waBusy, setWaBusy] = useState('');
  const [waMsg, setWaMsg] = useState('');
  const { 
    data: booking, 
    isLoading, 
    error,
    refetch,
  } = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => apiService.getBooking(bookingId),
    enabled: isOpen && !!bookingId,
  });

  const isLegacyToken = (token?: string) =>
    !!token &&
    (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(token) || token.length > 12);

  const hasLegacyBoardingCodes =
    !!booking &&
    (isLegacyToken(booking.contactPerson?.boardingToken) ||
      (booking.companions || []).some((c) => isLegacyToken(c.boardingToken)));

  useEffect(() => {
    if (!isOpen || !booking || !hasLegacyBoardingCodes) return;
    let cancelled = false;
    (async () => {
      try {
        await apiService.upgradeBoardingCodes();
        if (!cancelled) refetch();
      } catch (e) {
        console.error('Upgrade boarding codes failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, booking?._id, hasLegacyBoardingCodes, refetch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const handleSendWhatsApp = async (type: 'invoice' | 'reminder' | 'confirmation') => {
    if (!booking) return;
    setWaBusy(type);
    setWaMsg('');
    try {
      const res = await apiService.sendBookingWhatsApp(booking._id, type);
      setWaMsg(res.message || 'تم الإرسال');
    } catch (err: any) {
      setWaMsg(err?.response?.data?.message || err?.message || 'فشل الإرسال');
    } finally {
      setWaBusy('');
    }
  };

  const calculateAge = (birthDate: string | Date) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'pending': { label: 'في انتظار التأكيد', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      'confirmed': { label: 'مؤكد', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      'cancelled': { label: 'ملغي', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
      'completed': { label: 'مكتمل', color: 'bg-blue-100 text-blue-800', icon: CheckCircleIcon },
      'refunded': { label: 'مسترد', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800', 
      icon: ClockIcon 
    };
    
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {statusInfo.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusMap = {
      'pending': { label: 'في انتظار الدفع', color: 'bg-red-100 text-red-800', icon: ClockIcon },
      'partial': { label: 'مدفوع جزئياً', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      'paid': { label: 'مدفوع بالكامل', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      'refunded': { label: 'مسترد', color: 'bg-gray-100 text-gray-800', icon: XCircleIcon }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800', 
      icon: ClockIcon 
    };
    
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {statusInfo.label}
      </span>
    );
  };

  const getTripTypeText = (type: string) => {
    const typeMap = {
      'hajj': 'حج',
      'umrah': 'عمرة',
      'transport': 'نقل'
    };
    return typeMap[type as keyof typeof typeMap] || type;
  };

  // Helper function to get route information (supports both legacy and new format)
  const getBookingRouteInfo = () => {
    if (!booking) {
      return {
        from: 'غير محدد',
        to: 'غير محدد',
        departureDate: null,
        returnDate: null
      };
    }

    // If booking has new routes format, use the first active route
    if (booking.routes && booking.routes.length > 0) {
      const activeRoute = booking.routes.find(r => r.isActive) || booking.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDate: new Date(`${activeRoute.dateFrom}T${activeRoute.timeFrom}`),
        returnDate: new Date(`${activeRoute.dateTo}T${activeRoute.timeTo}`)
      };
    }
    
    // Fallback to legacy route format
    if (booking.route) {
      return {
        from: booking.route.from,
        to: booking.route.to,
        departureDate: booking.route.departureDate,
        returnDate: booking.route.returnDate
      };
    }
    
    // Default fallback
    return {
      from: 'غير محدد',
      to: 'غير محدد',
      departureDate: null,
      returnDate: null
    };
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap = {
      'low': { label: 'منخفضة', color: 'bg-gray-100 text-gray-800' },
      'normal': { label: 'عادية', color: 'bg-blue-100 text-blue-800' },
      'high': { label: 'عالية', color: 'bg-orange-100 text-orange-800' },
      'urgent': { label: 'عاجل', color: 'bg-red-100 text-red-800' }
    };
    
    const priorityInfo = priorityMap[priority as keyof typeof priorityMap] || { 
      label: priority, 
      color: 'bg-gray-100 text-gray-800' 
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${priorityInfo.color}`}>
        {priorityInfo.label}
      </span>
    );
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
          <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">جاري تحميل بيانات الحجز...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
          <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
            <div className="text-center py-12">
              <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600">حدث خطأ أثناء تحميل بيانات الحجز</p>
              <button onClick={onClose} className="mt-4 btn-primary">
                إغلاق
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-right align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4 space-x-reverse">
              <h2 className="text-2xl font-bold text-gray-900">
                تفاصيل الحجز
              </h2>
              <span className="text-lg font-mono text-primary-600">
                {booking.bookingNumber}
              </span>
            </div>
            <div className="flex items-center space-x-2 space-x-reverse">
              <button
                type="button"
                disabled={!!waBusy}
                onClick={() => handleSendWhatsApp('invoice')}
                className="btn-secondary text-sm inline-flex items-center gap-1"
                title="إرسال الفاتورة واتساب"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                {waBusy === 'invoice' ? 'جاري...' : 'فاتورة واتساب'}
              </button>
              <button
                type="button"
                disabled={!!waBusy}
                onClick={() => handleSendWhatsApp('reminder')}
                className="btn-secondary text-sm inline-flex items-center gap-1"
                title="إرسال تذكير واتساب"
              >
                <BellAlertIcon className="w-4 h-4" />
                {waBusy === 'reminder' ? 'جاري...' : 'تذكير واتساب'}
              </button>
              {onEdit && (
                <button
                  onClick={() => onEdit(booking)}
                  className="btn-secondary text-sm"
                >
                  تعديل
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {waMsg && (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {waMsg}
            </div>
          )}

          {booking.whatsappReminder?.enabled !== false && (
            <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-sm text-amber-900 text-right">
              تذكير واتساب تلقائي:{' '}
              {booking.whatsappReminder?.sentAt
                ? `تم الإرسال ${new Date(booking.whatsappReminder.sentAt).toLocaleString('ar-SA')}`
                : booking.whatsappReminder?.scheduledAt
                  ? `مجدول ${new Date(booking.whatsappReminder.scheduledAt).toLocaleString('ar-SA')} (قبل الرحلة بـ ${booking.whatsappReminder.hoursBeforeDeparture || 24} ساعة)`
                  : `مفعّل — قبل الرحلة بـ ${booking.whatsappReminder?.hoursBeforeDeparture || 24} ساعة`}
            </div>
          )}

          {booking.source === 'online' && (
            <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-right">
              <p className="text-sm font-semibold text-sky-900">حجز من رابط خارجي</p>
              <p className="mt-0.5 text-xs text-sky-700">
                وصل هذا الحجز عبر رابط العملاء الإلكتروني وبانتظار متابعة المكتب
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Trip Information */}
              <div className="card">
                <div className="flex items-center mb-4">
                  <MapPinIcon className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">معلومات الرحلة</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">نوع الرحلة</label>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {getTripTypeText(booking.tripType)}
                      </span>
                    </div>
                  </div>

                  {/* Show all routes if present */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500 mb-1">المسارات</label>
                    {booking.routes && booking.routes.length > 0 ? (
                      <div className="space-y-3">
                        {booking.routes.map((route, idx) => (
                          <div key={idx} className={`p-3 rounded border ${route.isActive ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">المسار {idx + 1}</span>
                              {route.isActive && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">نشط</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div><span className="text-gray-600">من:</span> {route.from}</div>
                              <div><span className="text-gray-600">إلى:</span> {route.to}</div>
                              <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                                <span className="text-gray-600">المغادرة:</span>
                                <DateDisplay value={route.dateFrom} />
                                <span className="text-gray-900">- {route.timeFrom}</span>
                              </div>
                              <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                                <span className="text-gray-600">الوصول:</span>
                                <DateDisplay value={route.dateTo} />
                                <span className="text-gray-900">- {route.timeTo}</span>
                              </div>
                              {route.distance && (
                                <div><span className="text-gray-600">المسافة:</span> {route.distance} كم</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : booking.route ? (
                      <div className="p-3 rounded border border-gray-200 bg-gray-50">
                        <div className="font-medium mb-2">المسار الرئيسي</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div><span className="text-gray-600">من:</span> {booking.route.from}</div>
                          <div><span className="text-gray-600">إلى:</span> {booking.route.to}</div>
                          <div><span className="text-gray-600">تاريخ المغادرة:</span> {String(booking.route.departureDate).split('T')[0]}</div>
                          <div><span className="text-gray-600">تاريخ العودة:</span> {String(booking.route.returnDate).split('T')[0]}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500">لا توجد مسارات مسجلة لهذا الحجز</div>
                    )}
                  </div>
                </div>

                {/* Services */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-500 mb-2">الخدمات المطلوبة</label>
                  <div className="flex flex-wrap gap-2">
                    {booking.services.transportation && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        <TruckIcon className="w-3 h-3 mr-1" />
                        النقل
                      </span>
                    )}
                    {booking.services.accommodation && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        <HomeIcon className="w-3 h-3 mr-1" />
                        الإقامة
                      </span>
                    )}
                    {booking.services.meals && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        الوجبات
                      </span>
                    )}
                    {booking.services.guide && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                        <UserGroupIcon className="w-3 h-3 mr-1" />
                        المرشد
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              <div className="card">
                <div className="flex items-center mb-4">
                  <UserIcon className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">بيانات الشخص المسؤول</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">الاسم الكامل</label>
                    <p className="mt-1 text-sm text-gray-900">{booking.contactPerson.name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">رقم الهوية</label>
                    <div className="mt-1 flex items-center">
                      <IdentificationIcon className="w-4 h-4 text-gray-400 ml-1" />
                      <span className="text-sm text-gray-900">{booking.contactPerson.idNumber}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">الجنسية</label>
                    <p className="mt-1 text-sm text-gray-900">{booking.contactPerson.nationality}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">رقم الجوال</label>
                    <div className="mt-1 flex items-center">
                      <PhoneIcon className="w-4 h-4 text-gray-400 ml-1" />
                      <span className="text-sm text-gray-900">{booking.contactPerson.phone}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">تاريخ الميلاد</label>
                    <p className="mt-1 text-sm text-gray-900">
                      <DateDisplay value={booking.contactPerson.birthDate} /> 
                      <span className="text-gray-500 mr-2">
                        ({calculateAge(booking.contactPerson.birthDate)} سنة)
                      </span>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">الجنس</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {booking.contactPerson.gender === 'male' ? 'ذكر' : 'أنثى'}
                    </p>
                  </div>
                  
                  {booking.contactPerson.email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">البريد الإلكتروني</label>
                      <p className="mt-1 text-sm text-gray-900">{booking.contactPerson.email}</p>
                    </div>
                  )}
                  
                  {booking.contactPerson.organization && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">المؤسسة</label>
                      <p className="mt-1 text-sm text-gray-900">{booking.contactPerson.organization}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Companions */}
              {booking.companions && booking.companions.length > 0 && (
                <div className="card">
                  <div className="flex items-center mb-4">
                    <UsersIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      المرافقون ({booking.companions.length})
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    {booking.companions.map((companion, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-900">{companion.name}</span>
                            <span className="mr-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              مرافق
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {companion.gender === 'male' ? 'ذكر' : 'أنثى'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">رقم الهوية:</span> {companion.idNumber}
                          </div>
                          <div>
                            <span className="font-medium">الجنسية:</span> {companion.nationality}
                          </div>
                          <div>
                            <span className="font-medium">العمر:</span> {calculateAge(companion.birthDate)} سنة
                          </div>
                          {companion.phone && (
                            <div>
                              <span className="font-medium">الجوال:</span> {companion.phone}
                            </div>
                          )}
                          {companion.seatNumber && (
                            <div>
                              <span className="font-medium">رقم المقعد:</span> {companion.seatNumber}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">تاريخ الميلاد:</span>{' '}
                            <DateDisplay value={companion.birthDate} className="inline-flex" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Boarding QR codes */}
              {booking.assignedTrip && (
                <div className="card">
                  <div className="flex items-center mb-4">
                    <QrCodeIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">رموز بوابة الصعود</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    رمز قصير من 5 أحرف فقط — امسح الـ QR أو أدخل الرمز عند الصعود (مثال: XK7M2).
                  </p>
                  {hasLegacyBoardingCodes && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      جاري تحويل الرموز الطويلة إلى رموز قصيرة…
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {booking.contactPerson.boardingToken && (
                      <div className="border border-gray-200 rounded-lg p-3 flex flex-col items-center">
                        <BoardingQrCode
                          token={booking.contactPerson.boardingToken}
                          label={`${booking.contactPerson.name}${
                            booking.contactPerson.seatNumber
                              ? ` — مقعد ${booking.contactPerson.seatNumber}`
                              : ''
                          }`}
                        />
                      </div>
                    )}
                    {(booking.companions || [])
                      .filter((c) => c.boardingToken)
                      .map((companion, index) => (
                        <div
                          key={companion.boardingToken || index}
                          className="border border-gray-200 rounded-lg p-3 flex flex-col items-center"
                        >
                          <BoardingQrCode
                            token={companion.boardingToken!}
                            label={`${companion.name}${
                              companion.seatNumber ? ` — مقعد ${companion.seatNumber}` : ''
                            }`}
                          />
                        </div>
                      ))}
                    {!booking.contactPerson.boardingToken &&
                      !(booking.companions || []).some((c) => c.boardingToken) && (
                        <p className="text-sm text-amber-700 col-span-full">
                          لا توجد رموز صعود بعد. احفظ الحجز مرة أخرى لتوليدها، أو أنشئ حجزاً جديداً.
                        </p>
                      )}
                  </div>
                </div>
              )}

              {/* Special Requests — hide auto-generated online-source placeholder */}
              {booking.specialRequests &&
                booking.specialRequests.trim() !== 'حجز إلكتروني عبر رابط العملاء' && (
                <div className="card">
                  <div className="flex items-center mb-4">
                    <DocumentTextIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">طلبات خاصة</h3>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{booking.specialRequests}</p>
                </div>
              )}

              {/* Payments History */}
              {booking.payments && booking.payments.length > 0 && (
                <div className="card">
                  <div className="flex items-center mb-4">
                    <CurrencyDollarIcon className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">سجل المدفوعات</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {booking.payments.map((payment, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900">
                                {formatCurrency(payment.amount)}
                              </span>
                              <span className="mr-2 text-sm text-gray-500">
                                ({payment.method === 'cash' ? 'نقدي' : 
                                  payment.method === 'card' ? 'بطاقة' : 'تحويل'})
                              </span>
                            </div>
                            {payment.reference && (
                              <p className="text-sm text-gray-600">
                                المرجع: {payment.reference}
                              </p>
                            )}
                            {payment.notes && (
                              <p className="text-sm text-gray-600">
                                ملاحظات: {payment.notes}
                              </p>
                            )}
                          </div>
                          <div className="text-left">
                            <p className="text-sm text-gray-500">
                              <DateDisplay value={payment.date} showTime />
                            </p>
                            {payment.receivedBy && (
                              <p className="text-xs text-gray-400">
                                {payment.receivedBy.fullName || payment.receivedBy.username}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status and Priority */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">الحالة والأولوية</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">حالة الحجز</label>
                    {getStatusBadge(booking.status)}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">حالة الدفع</label>
                    {getPaymentStatusBadge(booking.paymentStatus)}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">الأولوية</label>
                    {getPriorityBadge(booking.priority || 'normal')}
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="card">
                <div className="flex items-center mb-4">
                  <CurrencyDollarIcon className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">الملخص المالي</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">السعر الأساسي</span>
                    <span className="text-sm font-medium">{formatCurrency(booking.pricing.basePrice)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">رسوم النقل</span>
                    <span className="text-sm font-medium">{formatCurrency(booking.pricing.transportationFee)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">رسوم الإقامة</span>
                    <span className="text-sm font-medium">{formatCurrency(booking.pricing.accommodationFee)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">رسوم إضافية</span>
                    <span className="text-sm font-medium">{formatCurrency(booking.pricing.additionalFees)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">الخصم</span>
                    <span className="text-sm font-medium text-red-600">-{formatCurrency(booking.pricing.discount)}</span>
                  </div>
                  
                  <hr />
                  
                  <div className="flex justify-between">
                    <span className="font-medium">المبلغ الإجمالي</span>
                    <span className="font-bold text-lg">{formatCurrency(booking.pricing.totalAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">المبلغ المدفوع</span>
                    <span className="text-sm font-medium text-green-600">{formatCurrency(booking.pricing.paidAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">المبلغ المتبقي</span>
                    <span className="text-sm font-medium text-red-600">{formatCurrency(booking.pricing.remainingAmount)}</span>
                  </div>
                  
                  <hr />
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">عدد الأشخاص</span>
                    <span className="text-sm font-medium">
                      {1 + (booking.companions?.length || 0)} شخص
                    </span>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">معلومات الحجز</h3>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-600">أنشئ بواسطة:</span>
                    <p className="font-medium">
                      {typeof booking.createdBy === 'object' && booking.createdBy
                        ? (booking.createdBy.fullName || booking.createdBy.username || '—')
                        : '—'}
                    </p>
                  </div>

                  <div>
                    <span className="text-gray-600">تاريخ الإنشاء:</span>
                    <p className="font-medium"><DateDisplay value={booking.createdAt} showTime /></p>
                  </div>
                  
                  <div>
                    <span className="text-gray-600">آخر تحديث:</span>
                    <p className="font-medium"><DateDisplay value={booking.updatedAt} showTime /></p>
                  </div>
                  
                  {booking.branch && (
                    <div>
                      <span className="text-gray-600">الفرع:</span>
                      <p className="font-medium">{booking.branch.name}</p>
                    </div>
                  )}
                  
                  <div>
                    <span className="text-gray-600">طريقة الدفع:</span>
                    <p className="font-medium">
                      {booking.paymentMethod === 'cash' ? 'نقدي' :
                       booking.paymentMethod === 'card' ? 'بطاقة' :
                       booking.paymentMethod === 'transfer' ? 'تحويل' : 'مختلط'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Assigned Resources */}
              {(booking.assignedBus || booking.assignedTrip || booking.assignedAccommodation) && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">الموارد المخصصة</h3>
                  
                  <div className="space-y-3 text-sm">
                    {booking.assignedBus && (
                      <div>
                        <span className="text-gray-600">الحافلة:</span>
                        <p className="font-medium">
                          {booking.assignedBus.busNumber} - {booking.assignedBus.plateNumber}
                        </p>
                      </div>
                    )}
                    
                    {booking.assignedTrip && (
                      <div>
                        <span className="text-gray-600">الرحلة:</span>
                        <p className="font-medium">{booking.assignedTrip.tripNumber}</p>
                        {booking.isManual && (
                          <p className="text-xs text-gray-500 mt-0.5">مرتبطة تلقائياً من حجز يدوي</p>
                        )}
                      </div>
                    )}

                    {(() => {
                      const busId = booking.assignedBus?._id;
                      const entry = (booking.assignedTrip?.buses || []).find((b: any) => {
                        const id = b?.bus?._id || b?.bus;
                        return busId && id && String(id) === String(busId);
                      });
                      const staff = entry?.boardingStaff;
                      if (!staff) return null;
                      const name =
                        typeof staff === 'object'
                          ? staff.fullName || staff.username
                          : null;
                      if (!name) return null;
                      return (
                        <div>
                          <span className="text-gray-600">موظف بوابة الصعود:</span>
                          <p className="font-medium">{name}</p>
                        </div>
                      );
                    })()}
                    
                    {booking.assignedAccommodation && (
                      <div>
                        <span className="text-gray-600">الإقامة:</span>
                        <p className="font-medium">
                          {booking.assignedAccommodation.name} - {booking.assignedAccommodation.location}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails; 